import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Fields are underlined rows, not boxes.
 *
 * Screen System §5, and it is one of the most load-bearing rules in the system
 * because it is what stops a HOTTR form looking like every other dark dashboard.
 * Controls are typographic. A boxed input announces itself as a widget; an
 * underlined row lets the type carry the screen.
 *
 * Field states, all four, none of them red:
 *
 *   empty    32% white placeholder
 *   filled   white
 *   focused  pink rule + pink caret
 *   error    pink rule + one line of pink copy
 *
 * There is no red anywhere in HOTTR. Pink-only leaves no second colour for an
 * error, which is the point — error reads as emphasis, not alarm.
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 *
 * Stock shadcn's Input, shipped byte-identically by four apps:
 *
 *   flex h-10 w-full rounded-md border border-input bg-background px-3 py-2
 *
 * A 6px-cornered box at 40px high — under the 44px touch floor — with a full
 * border on all four sides.
 */

const FIELD = cn(
  'w-full bg-transparent px-0 text-[14px] leading-[1.55] text-white',
  // 44px floor. The stock h-10 was 40 and failed §5 on every form in the estate.
  'min-h-[44px]',
  // 32%, not 12%. With no box, the RULE is the whole affordance — at 12% it
  // read as a divider between rows rather than as the edge of a field. This is
  // the value the brand site drew (Screen System §5, and its own input code).
  'border-0 border-b border-white/32 rounded-none',
  'placeholder:text-white/32',
  'transition-[border-color,box-shadow] duration-base ease-mechanical',
  // Focus is the pink rule, not a ring — a ring would redraw the box this rule
  // exists to remove.
  'caret-hottr-pink focus:border-hottr-pink focus:outline-none focus:ring-0',
  'disabled:cursor-not-allowed disabled:opacity-40',
  // The one legal error colour. `aria-invalid` drives it so the visual state and
  // the accessible state cannot disagree.
  'aria-[invalid=true]:border-hottr-pink',
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type = 'text', ...rest }, ref) => (
    <input ref={ref} type={type} className={cn(FIELD, className)} {...rest} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 3, ...rest }, ref) => (
  <textarea ref={ref} rows={rows} className={cn(FIELD, 'py-3 resize-y', className)} {...rest} />
));
Textarea.displayName = 'Textarea';
